import type { Actor } from "@redotech/account-common/actor";
import { countryCodes } from "@redotech/locale/country-codes";
import { usaProvinceCodes } from "@redotech/locale/provinces";
import { Currency } from "@redotech/money/currencies";
import { ConjunctionMode } from "@redotech/redo-customer-segments-common/segments/segment-types";
import { WeightUnit } from "@redotech/redo-fulfillment-measurements-common/weight-conversion";
import * as zExt from "@redotech/rpc/ext";
import { ShopifyIdType } from "@redotech/shopify-util/id";
import { TemporalDayOfWeek } from "@redotech/util/temporal";
import { assertNever } from "@redotech/util/type";
import { createEnum } from "@redotech/zod-util/zod-enum";
import { ObjectId } from "bson";
import * as z from "zod";

export enum CheckoutTreeNodeType {
  SPLIT_TEST = "SPLIT_TEST",
  CHECKOUT_EXPERIENCE = "CHECKOUT_EXPERIENCE",
  SEGMENT = "SEGMENT",
  N_ARY_SEGMENT = "N_ARY_SEGMENT",
}

// ───────────────────────────────────────────────────────────────────────────────
//   The recursive union of all node types
// ───────────────────────────────────────────────────────────────────────────────
const CheckoutTreeNodeSchema_ = z.lazy(() =>
  z.union([
    CheckoutTreeCheckoutExperienceNodeSchema,
    CheckoutTreeSplitTestNodeSchema,
    CheckoutTreeSegmentNodeSchema,
    CheckoutTreeNarySegmentNodeSchema,
  ]),
);
export const CheckoutTreeNodeSchema: z.ZodType<CheckoutTreeNode> =
  CheckoutTreeNodeSchema_;
export type CheckoutTreeNode = z.infer<typeof CheckoutTreeNodeSchema_>;

const CheckoutTreeNodeOptionalIdSchema_ = z.lazy(() =>
  z.union([
    CheckoutTreeCheckoutExperienceNodeOptionalIdSchema,
    CheckoutTreeSplitTestNodeOptionalIdSchema,
    CheckoutTreeSegmentNodeOptionalIdSchema,
    CheckoutTreeNarySegmentNodeOptionalIdSchema,
  ]),
);
export const CheckoutTreeNodeOptionalIdSchema: z.ZodType<CheckoutTreeNodeOptionalId> =
  CheckoutTreeNodeOptionalIdSchema_;
export type CheckoutTreeNodeOptionalId = z.infer<
  typeof CheckoutTreeNodeOptionalIdSchema_
>;

// ───────────────────────────────────────────────────────────────────────────────
//   CheckoutExperience node (no recursion)
// ───────────────────────────────────────────────────────────────────────────────
const CheckoutTreeCheckoutExperienceNodeSchema_ = z.object({
  nodeType: z.literal(CheckoutTreeNodeType.CHECKOUT_EXPERIENCE),
  checkoutExperienceId: zExt.objectId(),
  _id: zExt.objectId(),
});
const CheckoutTreeCheckoutExperienceNodeSchema: z.ZodType<CheckoutTreeCheckoutExperienceNode> =
  CheckoutTreeCheckoutExperienceNodeSchema_;
export interface CheckoutTreeCheckoutExperienceNode extends z.infer<
  typeof CheckoutTreeCheckoutExperienceNodeSchema_
> {}

const CheckoutTreeCheckoutExperienceNodeOptionalIdSchema_ =
  CheckoutTreeCheckoutExperienceNodeSchema_.partial({ _id: true });
const CheckoutTreeCheckoutExperienceNodeOptionalIdSchema: z.ZodType<CheckoutTreeCheckoutExperienceNodeOptionalId> =
  CheckoutTreeCheckoutExperienceNodeOptionalIdSchema_;
export interface CheckoutTreeCheckoutExperienceNodeOptionalId extends z.infer<
  typeof CheckoutTreeCheckoutExperienceNodeOptionalIdSchema_
> {}

// ───────────────────────────────────────────────────────────────────────────────
//   SplitTest edge (recursive)
// ───────────────────────────────────────────────────────────────────────────────
const CheckoutTreeSplitTestEdgeSchema_ = z.object({
  childNode: CheckoutTreeNodeSchema,
  treatmentId: zExt.objectId(),
});
const CheckoutTreeSplitTestEdgeSchema: z.ZodType<CheckoutTreeSplitTestEdge> =
  CheckoutTreeSplitTestEdgeSchema_;
export interface CheckoutTreeSplitTestEdge extends z.infer<
  typeof CheckoutTreeSplitTestEdgeSchema_
> {}

const CheckoutTreeSplitTestEdgeOptionalIdSchema_ = z.object({
  childNode: CheckoutTreeNodeOptionalIdSchema,
  treatmentId: zExt.objectId(),
});
const CheckoutTreeSplitTestEdgeOptionalIdSchema: z.ZodType<CheckoutTreeSplitTestEdgeOptionalId> =
  CheckoutTreeSplitTestEdgeOptionalIdSchema_;
export interface CheckoutTreeSplitTestEdgeOptionalId extends z.infer<
  typeof CheckoutTreeSplitTestEdgeOptionalIdSchema_
> {}

// ───────────────────────────────────────────────────────────────────────────────
//   SplitTest node
// ───────────────────────────────────────────────────────────────────────────────
const CheckoutTreeSplitTestNodeSchema_ = z.object({
  nodeType: z.literal(CheckoutTreeNodeType.SPLIT_TEST),
  splitTestId: zExt.objectId(),
  edges: z.array(CheckoutTreeSplitTestEdgeSchema),
  _id: zExt.objectId(),
});
const CheckoutTreeSplitTestNodeSchema: z.ZodType<CheckoutTreeSplitTestNode> =
  CheckoutTreeSplitTestNodeSchema_;
export interface CheckoutTreeSplitTestNode extends z.infer<
  typeof CheckoutTreeSplitTestNodeSchema_
> {}

const CheckoutTreeSplitTestNodeOptionalIdSchema_ =
  CheckoutTreeSplitTestNodeSchema_.partial({ _id: true })
    .omit({ edges: true })
    .extend({ edges: z.array(CheckoutTreeSplitTestEdgeOptionalIdSchema) });
const CheckoutTreeSplitTestNodeOptionalIdSchema: z.ZodType<CheckoutTreeSplitTestNodeOptionalId> =
  CheckoutTreeSplitTestNodeOptionalIdSchema_;
export interface CheckoutTreeSplitTestNodeOptionalId extends z.infer<
  typeof CheckoutTreeSplitTestNodeOptionalIdSchema_
> {}

// ───────────────────────────────────────────────────────────────────────────────
//   Segments
// ───────────────────────────────────────────────────────────────────────────────
export enum SegmentableCheckoutCharacteristic {
  /**
   * The distance between the destination address and the fulfillment center.
   */
  DELIVERY_DISTANCE_MILES = "DELIVERY_DISTANCE_MILES",
  /**
   * Number of customer's previous orders.
   */
  ORDER_COUNT = "ORDER_COUNT",
  /**
   * Sum of the customer's historical purchases (shop currency).
   */
  HISTORICAL_ORDER_VALUE = "HISTORICAL_ORDER_VALUE",
  /**
   * The location of the delivery address.
   */
  DELIVERY_LOCATION = "DELIVERY_LOCATION",
  /**
   * The location of the delivery address.
   */
  DELIVERY_LOCATION_V02262026 = "DELIVERY_LOCATION_V02262026",
  /**
   * The location of the delivery address, expressed as a binary predicate over
   * one or more {@link DeliveryLocationRule}s (country + optional provinces).
   * Unlike {@link DELIVERY_LOCATION} this supports arbitrary (non-USA) provinces
   * per country, matching the targeting power of the n-ary
   * {@link DELIVERY_LOCATION_V02262026} segment. Intended for use as a compound
   * leaf, where the n-ary form cannot be nested.
   */
  DELIVERY_LOCATION_RULES = "DELIVERY_LOCATION_RULES",
  /**
   * Customer's tags.
   */
  CUSTOMER_TAGS = "CUSTOMER_TAGS",
  /**
   * Product tags.
   */
  PRODUCT_TAGS = "PRODUCT_TAGS",
  /**
   * Product vendor.
   */
  PRODUCT_VENDOR = "PRODUCT_VENDOR",
  /**
   * Product SKU.
   */
  PRODUCT_SKU = "PRODUCT_SKU",
  /**
   * Quantity of items in the cart (excluding products with SKU "x-redo").
   */
  ITEM_QUANTITY = "ITEM_QUANTITY",
  /**
   * The location of the origin address.
   */
  ORIGIN_LOCATION = "ORIGIN_LOCATION",
  /**
   * The customer's historical return rate based on address.
   */
  CUSTOMER_RETURN_RATE = "CUSTOMER_RETURN_RATE",
  /**
   * The time of day.
   */
  TIME_OF_DAY = "TIME_OF_DAY",
  /**
   * A datetime range.
   */
  DATETIME_RANGE = "DATETIME_RANGE",
  /**
   * The customer's checkout probability score (0-100).
   */
  CHECKOUT_PROBABILITY = "CHECKOUT_PROBABILITY",
  /**
   * Product collections.
   */
  PRODUCT_COLLECTIONS = "PRODUCT_COLLECTIONS",
  /**
   * Line item properties.
   */
  LINE_ITEM_PROPERTIES = "LINE_ITEM_PROPERTIES",
  /**
   * Product metafields.
   */
  PRODUCT_METAFIELDS = "PRODUCT_METAFIELDS",
  /**
   * Cart value.
   */
  CART_VALUE = "CART_VALUE",
  /**
   * Total weight of the shipment in grams (sum over the rate request's line
   * items). With split-shipping enabled, Shopify's CarrierService calls the
   * rates endpoint once per shipment group, so this is per-shipment, not
   * per-cart.
   */
  SHIPMENT_WEIGHT = "SHIPMENT_WEIGHT",
  /**
   * Whether shipment destination country differs from shipment origin country.
   */
  CROSS_BORDER_SHIPMENT = "CROSS_BORDER_SHIPMENT",
  /**
   * Whether the shipping address is a PO Box (best-effort detection).
   */
  PO_BOX_SHIPMENT = "PO_BOX_SHIPMENT",
  /**
   * Whether the merchant can ship LCG (DDP) to the destination country on any
   * of its enabled international carrier lanes.
   */
  LCG_ELIGIBLE_SHIPMENT = "LCG_ELIGIBLE_SHIPMENT",
  /**
   * Destination address fields (address1, address2, city, province, postal
   * code, country).
   */
  ADDRESS_PROPERTIES = "ADDRESS_PROPERTIES",
  /**
   * A compound predicate combining other (non-tag) segment characteristics with
   * 2-level AND/OR boolean logic and per-leaf negation. N-ary only.
   */
  COMPOUND = "COMPOUND",
}

export enum SegmentNumberOperator {
  LESS_THAN = "LESS_THAN",
  LESS_THAN_OR_EQUAL = "LESS_THAN_OR_EQUAL",
  EQUAL = "EQUAL",
  GREATER_THAN = "GREATER_THAN",
  GREATER_THAN_OR_EQUAL = "GREATER_THAN_OR_EQUAL",
}

export enum SegmentListSingleItemMembershipOperator {
  IN = "IN",
  NOT_IN = "NOT_IN",
}

export enum SegmentListMultipleItemMembershipOperator {
  ALL_IN = "ALL_IN",
  ANY_IN = "ANY_IN",
  NONE_IN = "NONE_IN",
}

export enum SegmentTimeOfDayOperator {
  BEFORE = "BEFORE",
  AFTER = "AFTER",
}

export enum SegmentStringOperator {
  EQUALS = "EQUALS",
  NOT_EQUALS = "NOT_EQUALS",
  CONTAINS = "CONTAINS",
  IS_CONTAINED_IN = "IS_CONTAINED_IN",
  NOT_CONTAINS = "NOT_CONTAINS",
  STARTS_WITH = "STARTS_WITH",
}

export enum AddressPropertyField {
  ADDRESS1 = "ADDRESS1",
  ADDRESS2 = "ADDRESS2",
  CITY = "CITY",
  PROVINCE = "PROVINCE",
  POSTAL_CODE = "POSTAL_CODE",
  COUNTRY = "COUNTRY",
  NAME = "NAME",
}

// ───────────────────────────────────────────────────────────────────────────────
//   Segment edge (recursive)
// ───────────────────────────────────────────────────────────────────────────────
const CheckoutTreeSegmentEdgeSchema_ = z.object({
  childNode: CheckoutTreeNodeSchema,
});
const CheckoutTreeSegmentEdgeSchema: z.ZodType<CheckoutTreeSegmentEdge> =
  CheckoutTreeSegmentEdgeSchema_;
export interface CheckoutTreeSegmentEdge extends z.infer<
  typeof CheckoutTreeSegmentEdgeSchema_
> {}
const CheckoutTreeSegmentEdgeOptionalIdSchema_ = z.object({
  childNode: CheckoutTreeNodeOptionalIdSchema,
});
const CheckoutTreeSegmentEdgeOptionalIdSchema: z.ZodType<CheckoutTreeSegmentEdgeOptionalId> =
  CheckoutTreeSegmentEdgeOptionalIdSchema_;
export interface CheckoutTreeSegmentEdgeOptionalId extends z.infer<
  typeof CheckoutTreeSegmentEdgeOptionalIdSchema_
> {}

// Delivery distance miles segment
const DeliveryDistanceMilesSegmentSchema_ = z.object({
  segmentableCharacteristic: z.literal(
    SegmentableCheckoutCharacteristic.DELIVERY_DISTANCE_MILES,
  ),
  miles: z.number(),
  operator: z.literal(SegmentNumberOperator.LESS_THAN_OR_EQUAL),
});
export const DeliveryDistanceMilesSegmentSchema: z.ZodType<DeliveryDistanceMilesSegment> =
  DeliveryDistanceMilesSegmentSchema_;
export interface DeliveryDistanceMilesSegment extends z.infer<
  typeof DeliveryDistanceMilesSegmentSchema_
> {}

// Order count segment
const OrderCountSegmentSchema_ = z.object({
  segmentableCharacteristic: z.literal(
    SegmentableCheckoutCharacteristic.ORDER_COUNT,
  ),
  numOrders: z.number(),
  operator: createEnum(SegmentNumberOperator, {
    enumName: "SegmentNumberOperator",
  }),
});
export const OrderCountSegmentSchema: z.ZodType<OrderCountSegment> =
  OrderCountSegmentSchema_;
export interface OrderCountSegment extends z.infer<
  typeof OrderCountSegmentSchema_
> {}

// Historical order value segment
const HistoricalOrderValueSegmentSchema_ = z.object({
  segmentableCharacteristic: z.literal(
    SegmentableCheckoutCharacteristic.HISTORICAL_ORDER_VALUE,
  ),
  amount: z.number().min(0),
  // Source-of-truth currency for `amount`. The DCF converts the customer's
  // amountSpent to this currency (via the shop currency) before comparing.
  currency: createEnum(Currency, { enumName: "Currency" }),
  operator: createEnum(SegmentNumberOperator, {
    enumName: "SegmentNumberOperator",
  }),
});
export const HistoricalOrderValueSegmentSchema: z.ZodType<HistoricalOrderValueSegment> =
  HistoricalOrderValueSegmentSchema_;
export interface HistoricalOrderValueSegment extends z.infer<
  typeof HistoricalOrderValueSegmentSchema_
> {}

// Customer return rate segment
const CustomerReturnRateSegmentSchema_ = z.object({
  segmentableCharacteristic: z.literal(
    SegmentableCheckoutCharacteristic.CUSTOMER_RETURN_RATE,
  ),
  returnRatePercentage: z.number(),
  operator: createEnum(SegmentNumberOperator, {
    enumName: "SegmentNumberOperator",
  }),
});
export const CustomerReturnRateSegmentSchema: z.ZodType<CustomerReturnRateSegment> =
  CustomerReturnRateSegmentSchema_;
export interface CustomerReturnRateSegment extends z.infer<
  typeof CustomerReturnRateSegmentSchema_
> {}

// Delivery location segment
const DeliveryLocationSegmentSchema_ = z.object({
  segmentableCharacteristic: z.literal(
    SegmentableCheckoutCharacteristic.DELIVERY_LOCATION,
  ),
  locationCountry: z.object({
    countries: z.array(createEnum(countryCodes, { enumName: "CountryCodes" })),
    operator: createEnum(SegmentListSingleItemMembershipOperator, {
      enumName: "SegmentListSingleItemMembershipOperator",
    }),
  }),
  // Right now, we only support USA provinces.
  locationProvince: z
    .object({
      provinces: z.array(
        createEnum(usaProvinceCodes, { enumName: "UsaProvinceCodes" }),
      ),
      operator: createEnum(SegmentListSingleItemMembershipOperator, {
        enumName: "SegmentListSingleItemMembershipOperator",
      }),
    })
    .optional(),
});
export const DeliveryLocationSegmentSchema: z.ZodType<DeliveryLocationSegment> =
  DeliveryLocationSegmentSchema_;
export interface DeliveryLocationSegment extends z.infer<
  typeof DeliveryLocationSegmentSchema_
> {}

// Delivery location rule — a country with optional province constraints
const DeliveryLocationRuleSchema_ = z.object({
  countryCode: createEnum(countryCodes, { enumName: "CountryCodes" }),
  provinces: z.array(z.string()).optional(),
});
export const DeliveryLocationRuleSchema: z.ZodType<DeliveryLocationRule> =
  DeliveryLocationRuleSchema_;
export interface DeliveryLocationRule extends z.infer<
  typeof DeliveryLocationRuleSchema_
> {}

// Delivery location rules segment — a *binary* predicate over one or more
// DeliveryLocationRules. Matches when the destination matches any rule (IN) or
// no rule (NOT_IN). Supports arbitrary per-country provinces, so it can target a
// real Shopify zone faithfully (the binary DeliveryLocationSegment only supports
// USA provinces). This is the form usable as a compound leaf.
const DeliveryLocationRulesSegmentSchema_ = z.object({
  segmentableCharacteristic: z.literal(
    SegmentableCheckoutCharacteristic.DELIVERY_LOCATION_RULES,
  ),
  locationRules: z.array(DeliveryLocationRuleSchema),
  operator: createEnum(SegmentListSingleItemMembershipOperator, {
    enumName: "SegmentListSingleItemMembershipOperator",
  }),
});
export const DeliveryLocationRulesSegmentSchema: z.ZodType<DeliveryLocationRulesSegment> =
  DeliveryLocationRulesSegmentSchema_;
export interface DeliveryLocationRulesSegment extends z.infer<
  typeof DeliveryLocationRulesSegmentSchema_
> {}

// Customer tags segment
/**
 * Due to limitations in Shopify's delivery customization function, we can only
 * support this subset of multiple item list membership operators for customer
 * tags segments.
 */
export const CustomerTagsSegmentListMultipleItemMembershipOperator = [
  SegmentListMultipleItemMembershipOperator.ANY_IN,
  SegmentListMultipleItemMembershipOperator.NONE_IN,
] as const;
export type CustomerTagsSegmentListMultipleItemMembershipOperator =
  (typeof CustomerTagsSegmentListMultipleItemMembershipOperator)[number];

/**
 * Due to limitations in Shopify's delivery customization function, we only allow
 * two customer tags segments nodes in a checkout tree for now.
 */
export enum CustomerTagsSegmentNodeIdentifier {
  CUSTOMER_TAGS_SEGMENT_NODE_ONE = "CUSTOMER_TAGS_SEGMENT_NODE_ONE",
  CUSTOMER_TAGS_SEGMENT_NODE_TWO = "CUSTOMER_TAGS_SEGMENT_NODE_TWO",
}

/**
 * Exported as the raw object so surfaces that must republish this shape without
 * some of its fields can derive it with `.omit()` rather than restating it. The
 * `z.ZodType`-annotated alias below cannot: `.omit()` is a `ZodObject` method.
 */
export const CustomerTagsSegmentSchema_ = z.object({
  segmentableCharacteristic: z.literal(
    SegmentableCheckoutCharacteristic.CUSTOMER_TAGS,
  ),
  tags: z.array(z.string()),
  operator: createEnum(CustomerTagsSegmentListMultipleItemMembershipOperator, {
    enumName: "CustomerTagsSegmentListMultipleItemMembershipOperator",
  }),
  customerTagsSegmentNodeIdentifier: createEnum(
    CustomerTagsSegmentNodeIdentifier,
    { enumName: "CustomerTagsSegmentNodeIdentifier" },
  ),
});
export const CustomerTagsSegmentSchema: z.ZodType<CustomerTagsSegment> =
  CustomerTagsSegmentSchema_;
export interface CustomerTagsSegment extends z.infer<
  typeof CustomerTagsSegmentSchema_
> {}

// Product tags segment
/**
 * Due to limitations in Shopify's delivery customization function, we can only
 * support this subset of multiple item list membership operators for product
 * tags segments.
 */
export const ProductTagsSegmentListMultipleItemMembershipOperator = [
  SegmentListMultipleItemMembershipOperator.ANY_IN,
  SegmentListMultipleItemMembershipOperator.NONE_IN,
] as const;
export type ProductTagsSegmentListMultipleItemMembershipOperator =
  (typeof ProductTagsSegmentListMultipleItemMembershipOperator)[number];

/**
 * Due to limitations in Shopify's delivery customization function, we only allow
 * two product tags segment nodes in a checkout tree for now.
 */
export enum ProductTagsSegmentNodeIdentifier {
  PRODUCT_TAGS_SEGMENT_NODE_ONE = "PRODUCT_TAGS_SEGMENT_NODE_ONE",
  PRODUCT_TAGS_SEGMENT_NODE_TWO = "PRODUCT_TAGS_SEGMENT_NODE_TWO",
}

/**
 * The maximum number of unique product tags segment nodes allowed in a checkout
 * tree without subscriptions mode. Derived from the number of available
 * identifiers, which is constrained by Shopify's delivery customization
 * function input variables.
 */
export const MAX_PRODUCT_TAGS_SEGMENT_NODES = Object.values(
  ProductTagsSegmentNodeIdentifier,
).length;

/**
 * Exported as the raw object so surfaces that must republish this shape without
 * some of its fields can derive it with `.omit()` rather than restating it. The
 * `z.ZodType`-annotated alias below cannot: `.omit()` is a `ZodObject` method.
 */
export const ProductTagsSegmentSchema_ = z.object({
  segmentableCharacteristic: z.literal(
    SegmentableCheckoutCharacteristic.PRODUCT_TAGS,
  ),
  tags: z.array(z.string()),
  operator: createEnum(ProductTagsSegmentListMultipleItemMembershipOperator, {
    enumName: "ProductTagsSegmentListMultipleItemMembershipOperator",
  }),
  cartOperator: createEnum(SegmentListMultipleItemMembershipOperator, {
    enumName: "SegmentListMultipleItemMembershipOperator",
  }),
  productTagsSegmentNodeIdentifier: createEnum(
    ProductTagsSegmentNodeIdentifier,
    { enumName: "ProductTagsSegmentNodeIdentifier" },
  ),
});
export const ProductTagsSegmentSchema: z.ZodType<ProductTagsSegment> =
  ProductTagsSegmentSchema_;
export interface ProductTagsSegment extends z.infer<
  typeof ProductTagsSegmentSchema_
> {}

// Product vendor segment
/**
 * Product vendors list with list membership operators for product vendor segments.
 */
const ProductVendorSegmentSchema_ = z.object({
  segmentableCharacteristic: z.literal(
    SegmentableCheckoutCharacteristic.PRODUCT_VENDOR,
  ),
  vendors: z.array(z.string()),
  operator: createEnum(SegmentListSingleItemMembershipOperator, {
    enumName: "SegmentListSingleItemMembershipOperator",
  }),
  cartOperator: createEnum(SegmentListMultipleItemMembershipOperator, {
    enumName: "SegmentListMultipleItemMembershipOperator",
  }),
});
export const ProductVendorSegmentSchema: z.ZodType<ProductVendorSegment> =
  ProductVendorSegmentSchema_;
export interface ProductVendorSegment extends z.infer<
  typeof ProductVendorSegmentSchema_
> {}

// Product SKU segment
/**
 * Product SKUs list with list membership operators for product SKU segments.
 */
const ProductSkuSegmentSchema_ = z.object({
  segmentableCharacteristic: z.literal(
    SegmentableCheckoutCharacteristic.PRODUCT_SKU,
  ),
  skus: z.array(z.string()),
  operator: createEnum(SegmentListSingleItemMembershipOperator, {
    enumName: "SegmentListSingleItemMembershipOperator",
  }),
  cartOperator: createEnum(SegmentListMultipleItemMembershipOperator, {
    enumName: "SegmentListMultipleItemMembershipOperator",
  }),
  requireExactMatch: z.boolean(),
});
export const ProductSkuSegmentSchema: z.ZodType<ProductSkuSegment> =
  ProductSkuSegmentSchema_;
export interface ProductSkuSegment extends z.infer<
  typeof ProductSkuSegmentSchema_
> {}

// Item quantity segment
const ItemQuantitySegmentSchema_ = z.object({
  segmentableCharacteristic: z.literal(
    SegmentableCheckoutCharacteristic.ITEM_QUANTITY,
  ),
  quantity: z.number(),
  operator: createEnum(SegmentNumberOperator, {
    enumName: "SegmentNumberOperator",
  }),
  exclusions: z.object({ skus: z.array(z.string()).optional() }).optional(),
});
export const ItemQuantitySegmentSchema: z.ZodType<ItemQuantitySegment> =
  ItemQuantitySegmentSchema_;
export interface ItemQuantitySegment extends z.infer<
  typeof ItemQuantitySegmentSchema_
> {}

// Origin location segment
const OriginLocationSegmentSchema_ = z.object({
  segmentableCharacteristic: z.literal(
    SegmentableCheckoutCharacteristic.ORIGIN_LOCATION,
  ),
  originAddress: z.object({
    address1: z.string().nullish(),
    address2: z.string().nullish(),
    city: z.string().nullish(),
    province: z.string().nullish(),
    postalCode: z.string().nullish(),
    country: z.string().nullish(),
    latitude: z.number().nullish(),
    longitude: z.number().nullish(),
  }),
});
export const OriginLocationSegmentSchema: z.ZodType<OriginLocationSegment> =
  OriginLocationSegmentSchema_;
export interface OriginLocationSegment extends z.infer<
  typeof OriginLocationSegmentSchema_
> {}

// Time of day segment
const TimeOfDaySegmentSchema_ = z.object({
  segmentableCharacteristic: z.literal(
    SegmentableCheckoutCharacteristic.TIME_OF_DAY,
  ),
  /**
   * The time of day with hours and minutes.
   */
  time: z.object({
    hh: z.number().int().min(0).max(23),
    mm: z.number().int().min(0).max(59),
  }),
  /**
   * The timezone in IANA timezone identifier format.
   */
  ianaTimezone: z.string(),
  operator: createEnum(SegmentTimeOfDayOperator, {
    enumName: "SegmentTimeOfDayOperator",
  }),
  /**
   * Weekdays filter. The condition only matches on the specified weekdays.
   */
  weekdays: z
    .array(createEnum(TemporalDayOfWeek, { enumName: "TemporalDayOfWeek" }))
    .min(1),
});
export const TimeOfDaySegmentSchema: z.ZodType<TimeOfDaySegment> =
  TimeOfDaySegmentSchema_;
export interface TimeOfDaySegment extends z.infer<
  typeof TimeOfDaySegmentSchema_
> {}

// Datetime range segment
const DatetimeRangeSegmentSchema_ = z.object({
  segmentableCharacteristic: z.literal(
    SegmentableCheckoutCharacteristic.DATETIME_RANGE,
  ),
  startDateTime: z.string(),
  endDateTime: z.string(),
  /**
   * The timezone in IANA timezone identifier format.
   */
  ianaTimezone: z.string(),
});
export const DatetimeRangeSegmentSchema: z.ZodType<DatetimeRangeSegment> =
  DatetimeRangeSegmentSchema_;
export interface DatetimeRangeSegment extends z.infer<
  typeof DatetimeRangeSegmentSchema_
> {}

// Checkout probability segment
const CheckoutProbabilitySegmentSchema_ = z.object({
  segmentableCharacteristic: z.literal(
    SegmentableCheckoutCharacteristic.CHECKOUT_PROBABILITY,
  ),
  expressions: z
    .array(
      z.object({
        probability: z.number().min(0).max(100),
        operator: createEnum(SegmentNumberOperator, {
          enumName: "SegmentNumberOperator",
        }),
      }),
    )
    .min(1)
    .max(2),
});
export const CheckoutProbabilitySegmentSchema: z.ZodType<CheckoutProbabilitySegment> =
  CheckoutProbabilitySegmentSchema_;
export interface CheckoutProbabilitySegment extends z.infer<
  typeof CheckoutProbabilitySegmentSchema_
> {}

// Product collections segment
const ProductCollectionsSegmentSchema_ = z.object({
  segmentableCharacteristic: z.literal(
    SegmentableCheckoutCharacteristic.PRODUCT_COLLECTIONS,
  ),
  collectionIds: z.array(z.string()),
  collectionListOperator: createEnum(
    SegmentListMultipleItemMembershipOperator,
    { enumName: "SegmentListMultipleItemMembershipOperator" },
  ),
  cartOperator: createEnum(SegmentListMultipleItemMembershipOperator, {
    enumName: "SegmentListMultipleItemMembershipOperator",
  }),
});
export const ProductCollectionsSegmentSchema: z.ZodType<ProductCollectionsSegment> =
  ProductCollectionsSegmentSchema_;
export interface ProductCollectionsSegment extends z.infer<
  typeof ProductCollectionsSegmentSchema_
> {}

// Line item properties segment
export const segmentPropertyMatchMode = ["exact", "wildcard"] as const;
export type SegmentPropertyMatchMode =
  (typeof segmentPropertyMatchMode)[number];

const LineItemPropertiesSegmentSchema_ = z.object({
  segmentableCharacteristic: z.literal(
    SegmentableCheckoutCharacteristic.LINE_ITEM_PROPERTIES,
  ),
  properties: z.array(
    z.object({
      key: z.string(),
      value: z.string(),
      matchMode: createEnum(segmentPropertyMatchMode, {
        enumName: "SegmentPropertyMatchMode",
      }).optional(),
    }),
  ),
  propertiesListOperator: createEnum(
    SegmentListMultipleItemMembershipOperator,
    { enumName: "SegmentListMultipleItemMembershipOperator" },
  ),
  cartOperator: createEnum(SegmentListMultipleItemMembershipOperator, {
    enumName: "SegmentListMultipleItemMembershipOperator",
  }),
});
export const LineItemPropertiesSegmentSchema: z.ZodType<LineItemPropertiesSegment> =
  LineItemPropertiesSegmentSchema_;
export interface LineItemPropertiesSegment extends z.infer<
  typeof LineItemPropertiesSegmentSchema_
> {}

// Product metafields segment
/**
 * Product metafields list with membership operators for product metafield segments.
 * Metafields are key-value pairs with namespace for organization.
 * Supports both product-level and variant-level metafields.
 */
const ProductMetafieldsSegmentSchema_ = z.object({
  segmentableCharacteristic: z.literal(
    SegmentableCheckoutCharacteristic.PRODUCT_METAFIELDS,
  ),
  metafields: z.array(
    z.object({
      resourceType: z.union([
        z.literal(ShopifyIdType.Product),
        z.literal(ShopifyIdType.ProductVariant),
      ]),
      namespace: z.string(),
      key: z.string(),
      value: z.string(),
      valueMatchKind: z
        .union([z.literal("exact"), z.literal("partial")])
        .optional(),
    }),
  ),
  operator: createEnum(SegmentListMultipleItemMembershipOperator, {
    enumName: "SegmentListMultipleItemMembershipOperator",
  }),
  cartOperator: createEnum(SegmentListMultipleItemMembershipOperator, {
    enumName: "SegmentListMultipleItemMembershipOperator",
  }),
});
export const ProductMetafieldsSegmentSchema: z.ZodType<ProductMetafieldsSegment> =
  ProductMetafieldsSegmentSchema_;
export interface ProductMetafieldsSegment extends z.infer<
  typeof ProductMetafieldsSegmentSchema_
> {}

// Cart value segment
export enum CartValueKind {
  TOTAL = "TOTAL",
  DISCOUNTED = "DISCOUNTED",
}

const CartValueSegmentSchema_ = z.object({
  segmentableCharacteristic: z.literal(
    SegmentableCheckoutCharacteristic.CART_VALUE,
  ),
  value: z.object({
    /**
     * Assumed to be shop currency.
     */
    amount: z.number(),
  }),
  operator: createEnum(SegmentNumberOperator, {
    enumName: "SegmentNumberOperator",
  }),
  valueKind: createEnum(CartValueKind, { enumName: "CartValueKind" }),
});
export const CartValueSegmentSchema: z.ZodType<CartValueSegment> =
  CartValueSegmentSchema_;
export interface CartValueSegment extends z.infer<
  typeof CartValueSegmentSchema_
> {}

const ShipmentWeightSegmentSchema_ = z.object({
  segmentableCharacteristic: z.literal(
    SegmentableCheckoutCharacteristic.SHIPMENT_WEIGHT,
  ),
  weight: z.number().nonnegative(),
  unit: createEnum(WeightUnit, { enumName: "WeightUnit" }),
  operator: createEnum(SegmentNumberOperator, {
    enumName: "SegmentNumberOperator",
  }),
});
export const ShipmentWeightSegmentSchema: z.ZodType<ShipmentWeightSegment> =
  ShipmentWeightSegmentSchema_;
export interface ShipmentWeightSegment extends z.infer<
  typeof ShipmentWeightSegmentSchema_
> {}

// Cross-border shipment segment
const CrossBorderShipmentSegmentSchema_ = z.object({
  segmentableCharacteristic: z.literal(
    SegmentableCheckoutCharacteristic.CROSS_BORDER_SHIPMENT,
  ),
});
export const CrossBorderShipmentSegmentSchema: z.ZodType<CrossBorderShipmentSegment> =
  CrossBorderShipmentSegmentSchema_;
export interface CrossBorderShipmentSegment extends z.infer<
  typeof CrossBorderShipmentSegmentSchema_
> {}

// LCG eligible shipment segment
const LcgEligibleShipmentSegmentSchema_ = z.object({
  segmentableCharacteristic: z.literal(
    SegmentableCheckoutCharacteristic.LCG_ELIGIBLE_SHIPMENT,
  ),
});
export const LcgEligibleShipmentSegmentSchema: z.ZodType<LcgEligibleShipmentSegment> =
  LcgEligibleShipmentSegmentSchema_;
export interface LcgEligibleShipmentSegment extends z.infer<
  typeof LcgEligibleShipmentSegmentSchema_
> {}

// PO box shipment segment
const PoBoxShipmentSegmentSchema_ = z.object({
  segmentableCharacteristic: z.literal(
    SegmentableCheckoutCharacteristic.PO_BOX_SHIPMENT,
  ),
});
export const PoBoxShipmentSegmentSchema: z.ZodType<PoBoxShipmentSegment> =
  PoBoxShipmentSegmentSchema_;
export interface PoBoxShipmentSegment extends z.infer<
  typeof PoBoxShipmentSegmentSchema_
> {}

// Address properties segment — destination address branching with one or more
// AND-ed string rules.
const AddressPropertiesSegmentRuleSchema_ = z.object({
  field: createEnum(AddressPropertyField, { enumName: "AddressPropertyField" }),
  operator: createEnum(SegmentStringOperator, {
    enumName: "SegmentStringOperator",
  }),
  value: z.string(),
});
export const AddressPropertiesSegmentRuleSchema: z.ZodType<AddressPropertiesSegmentRule> =
  AddressPropertiesSegmentRuleSchema_;
export interface AddressPropertiesSegmentRule extends z.infer<
  typeof AddressPropertiesSegmentRuleSchema_
> {}

const AddressPropertiesSegmentSchema_ = z.object({
  segmentableCharacteristic: z.literal(
    SegmentableCheckoutCharacteristic.ADDRESS_PROPERTIES,
  ),
  rules: z.array(AddressPropertiesSegmentRuleSchema).min(1),
  conjunction: createEnum(ConjunctionMode, {
    enumName: "ConjunctionMode",
  }).default(ConjunctionMode.AND),
});
export const AddressPropertiesSegmentSchema: z.ZodType<AddressPropertiesSegment> =
  AddressPropertiesSegmentSchema_;
export interface AddressPropertiesSegment extends z.infer<
  typeof AddressPropertiesSegmentSchema_
> {}

export const SegmentSchema = z.union([
  DeliveryDistanceMilesSegmentSchema,
  OrderCountSegmentSchema,
  CustomerReturnRateSegmentSchema,
  DeliveryLocationSegmentSchema,
  DeliveryLocationRulesSegmentSchema,
  CustomerTagsSegmentSchema,
  ProductTagsSegmentSchema,
  ProductVendorSegmentSchema,
  ProductSkuSegmentSchema,
  ItemQuantitySegmentSchema,
  OriginLocationSegmentSchema,
  TimeOfDaySegmentSchema,
  DatetimeRangeSegmentSchema,
  CheckoutProbabilitySegmentSchema,
  ProductCollectionsSegmentSchema,
  LineItemPropertiesSegmentSchema,
  ProductMetafieldsSegmentSchema,
  CartValueSegmentSchema,
  ShipmentWeightSegmentSchema,
  CrossBorderShipmentSegmentSchema,
  PoBoxShipmentSegmentSchema,
  LcgEligibleShipmentSegmentSchema,
  AddressPropertiesSegmentSchema,
  HistoricalOrderValueSegmentSchema,
]);
export type Segment = z.infer<typeof SegmentSchema>;

// ───────────────────────────────────────────────────────────────────────────────
//   Segment node
// ───────────────────────────────────────────────────────────────────────────────
const CheckoutTreeSegmentNodeSchema_ = z.object({
  nodeType: z.literal(CheckoutTreeNodeType.SEGMENT),
  matchingEdge: CheckoutTreeSegmentEdgeSchema,
  nonMatchingEdge: CheckoutTreeSegmentEdgeSchema,
  segment: SegmentSchema,
  _id: zExt.objectId(),
  name: z.string().nullish(),
});
const CheckoutTreeSegmentNodeSchema: z.ZodType<CheckoutTreeSegmentNode> =
  CheckoutTreeSegmentNodeSchema_;
export interface CheckoutTreeSegmentNode extends z.infer<
  typeof CheckoutTreeSegmentNodeSchema_
> {}

const CheckoutTreeSegmentNodeOptionalIdSchema_ =
  CheckoutTreeSegmentNodeSchema_.partial({ _id: true })
    .omit({ matchingEdge: true, nonMatchingEdge: true })
    .extend({
      matchingEdge: CheckoutTreeSegmentEdgeOptionalIdSchema,
      nonMatchingEdge: CheckoutTreeSegmentEdgeOptionalIdSchema,
    });
const CheckoutTreeSegmentNodeOptionalIdSchema: z.ZodType<CheckoutTreeSegmentNodeOptionalId> =
  CheckoutTreeSegmentNodeOptionalIdSchema_;
export interface CheckoutTreeSegmentNodeOptionalId extends z.infer<
  typeof CheckoutTreeSegmentNodeOptionalIdSchema_
> {}

// ───────────────────────────────────────────────────────────────────────────────
//   N-ary segment edge (recursive) — just a path to a child node
// ───────────────────────────────────────────────────────────────────────────────
const CheckoutTreeNarySegmentEdgeSchema_ = z.object({
  _id: zExt.objectId(),
  childNode: CheckoutTreeNodeSchema,
});
const CheckoutTreeNarySegmentEdgeSchema: z.ZodType<CheckoutTreeNarySegmentEdge> =
  CheckoutTreeNarySegmentEdgeSchema_;
export interface CheckoutTreeNarySegmentEdge extends z.infer<
  typeof CheckoutTreeNarySegmentEdgeSchema_
> {}

const CheckoutTreeNarySegmentEdgeOptionalIdSchema_ = z.object({
  _id: zExt.objectId().optional(),
  childNode: CheckoutTreeNodeOptionalIdSchema,
});
const CheckoutTreeNarySegmentEdgeOptionalIdSchema: z.ZodType<CheckoutTreeNarySegmentEdgeOptionalId> =
  CheckoutTreeNarySegmentEdgeOptionalIdSchema_;
export interface CheckoutTreeNarySegmentEdgeOptionalId extends z.infer<
  typeof CheckoutTreeNarySegmentEdgeOptionalIdSchema_
> {}

// ───────────────────────────────────────────────────────────────────────────────
//   N-ary segment — the decision data that determines which edge to traverse.
//   Each condition references an edge by ID. Conditions are evaluated in array
//   order; the first match's edgeId is used. If none match, defaultEdgeId is
//   used. The traversal function returns edgeId | undefined.
// ───────────────────────────────────────────────────────────────────────────────

// Delivery location v02262026 — supports multiple countries with correlated provinces
/**
 * Exported as the raw object so surfaces that must republish this shape without
 * some of its fields can derive it with `.omit()` rather than restating it. The
 * `z.ZodType`-annotated alias below cannot: `.omit()` is a `ZodObject` method.
 */
export const DeliveryLocationConditionSchema_ = z.object({
  edgeId: zExt.objectId(),
  locationRules: z.array(DeliveryLocationRuleSchema),
});
export const DeliveryLocationConditionSchema: z.ZodType<DeliveryLocationCondition> =
  DeliveryLocationConditionSchema_;
export interface DeliveryLocationCondition extends z.infer<
  typeof DeliveryLocationConditionSchema_
> {}

const DeliveryLocationNarySegmentSchema_ = z.object({
  segmentableCharacteristic: z.literal(
    SegmentableCheckoutCharacteristic.DELIVERY_LOCATION_V02262026,
  ),
  conditions: z.array(DeliveryLocationConditionSchema),
});
export const DeliveryLocationNarySegmentSchema: z.ZodType<DeliveryLocationNarySegment> =
  DeliveryLocationNarySegmentSchema_;
export interface DeliveryLocationNarySegment extends z.infer<
  typeof DeliveryLocationNarySegmentSchema_
> {}

// ───────────────────────────────────────────────────────────────────────────────
//   Compound n-ary segment
//
//   A 2-level boolean predicate (DNF/CNF over negatable leaves) per routing
//   edge. Leaves reuse the existing *binary* SegmentSchema union, so there is no
//   recursion — a compound cannot nest another compound or an n-ary-only
//   segment. Conditions are evaluated in array order; the first whose predicate
//   is TRUE selects that edgeId. If none match, the node's defaultEdgeId is used.
// ───────────────────────────────────────────────────────────────────────────────

// A compound is always resolved at the rates endpoint (it never participates in
// the Shopify Delivery Customization function), so its leaves are restricted to
// characteristics that are always decidable there. The excluded ones are either
// decided later in the Delivery Customization function or not reliably available
// at rate time:
//   - CUSTOMER_TAGS / PRODUCT_TAGS — consume the scarce 4-slot input budget and
//     are evaluated in the Delivery Customization function.
//   - HISTORICAL_ORDER_VALUE / ORDER_COUNT — customer history; ORDER_COUNT is
//     only rate-known in subscriptions mode, HISTORICAL_ORDER_VALUE never is.
//   - CHECKOUT_PROBABILITY — not guaranteed to be present on the rate request.
export const COMPOUND_FORBIDDEN_LEAF_CHARACTERISTICS = [
  SegmentableCheckoutCharacteristic.CUSTOMER_TAGS,
  SegmentableCheckoutCharacteristic.PRODUCT_TAGS,
  SegmentableCheckoutCharacteristic.HISTORICAL_ORDER_VALUE,
  SegmentableCheckoutCharacteristic.ORDER_COUNT,
  SegmentableCheckoutCharacteristic.CHECKOUT_PROBABILITY,
] as const;

// A single, optionally-negated condition reusing any binary Segment as its
// predicate.
const CompoundSegmentLeafSchema_ = z
  .object({ negate: z.boolean().default(false), segment: SegmentSchema })
  .superRefine((leaf, ctx) => {
    if (
      (
        COMPOUND_FORBIDDEN_LEAF_CHARACTERISTICS as readonly SegmentableCheckoutCharacteristic[]
      ).includes(leaf.segment.segmentableCharacteristic)
    ) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: `${leaf.segment.segmentableCharacteristic} is not allowed inside a compound segment`,
        path: ["segment", "segmentableCharacteristic"],
      });
    }
  });
export const CompoundSegmentLeafSchema: z.ZodType<CompoundSegmentLeaf> =
  CompoundSegmentLeafSchema_;
export interface CompoundSegmentLeaf extends z.infer<
  typeof CompoundSegmentLeafSchema_
> {}

// Inner level: a group of leaves combined by one conjunction.
const CompoundSegmentGroupSchema_ = z.object({
  conjunction: createEnum(ConjunctionMode, { enumName: "ConjunctionMode" }),
  leaves: z.array(CompoundSegmentLeafSchema).min(1),
});
export const CompoundSegmentGroupSchema: z.ZodType<CompoundSegmentGroup> =
  CompoundSegmentGroupSchema_;
export interface CompoundSegmentGroup extends z.infer<
  typeof CompoundSegmentGroupSchema_
> {}

// Outer level: groups combined by one conjunction → the predicate for one edge.
/**
 * Exported as the raw object so surfaces that must republish this shape without
 * some of its fields can derive it with `.omit()` rather than restating it. The
 * `z.ZodType`-annotated alias below cannot: `.omit()` is a `ZodObject` method.
 */
export const CompoundSegmentConditionSchema_ = z.object({
  edgeId: zExt.objectId(),
  name: z.string().nullish(),
  conjunction: createEnum(ConjunctionMode, { enumName: "ConjunctionMode" }),
  groups: z.array(CompoundSegmentGroupSchema).min(1),
});
export const CompoundSegmentConditionSchema: z.ZodType<CompoundSegmentCondition> =
  CompoundSegmentConditionSchema_;
export interface CompoundSegmentCondition extends z.infer<
  typeof CompoundSegmentConditionSchema_
> {}

const CompoundNarySegmentSchema_ = z.object({
  segmentableCharacteristic: z.literal(
    SegmentableCheckoutCharacteristic.COMPOUND,
  ),
  conditions: z.array(CompoundSegmentConditionSchema).min(1),
});
export const CompoundNarySegmentSchema: z.ZodType<CompoundNarySegment> =
  CompoundNarySegmentSchema_;
export interface CompoundNarySegment extends z.infer<
  typeof CompoundNarySegmentSchema_
> {}

const CheckoutTreeNarySegmentSchema = z.union([
  DeliveryLocationNarySegmentSchema,
  CompoundNarySegmentSchema,
]);
export type CheckoutTreeNarySegment = z.infer<
  typeof CheckoutTreeNarySegmentSchema
>;

/**
 * Returns the set of leaf characteristics referenced anywhere in a compound
 * segment, so the rates endpoint can lazy-load only the (often expensive)
 * attributes it must compute to resolve the compound.
 */
export function getCompoundRequiredCharacteristics(
  segment: CompoundNarySegment,
): Set<SegmentableCheckoutCharacteristic> {
  const characteristics = new Set<SegmentableCheckoutCharacteristic>();
  for (const condition of segment.conditions) {
    for (const group of condition.groups) {
      for (const leaf of group.leaves) {
        characteristics.add(leaf.segment.segmentableCharacteristic);
      }
    }
  }
  return characteristics;
}

// ───────────────────────────────────────────────────────────────────────────────
//   N-ary segment node
// ───────────────────────────────────────────────────────────────────────────────
const CheckoutTreeNarySegmentNodeSchema_ = z.object({
  nodeType: z.literal(CheckoutTreeNodeType.N_ARY_SEGMENT),
  edges: z.array(CheckoutTreeNarySegmentEdgeSchema),
  defaultEdgeId: zExt.objectId(),
  segment: CheckoutTreeNarySegmentSchema,
  _id: zExt.objectId(),
  name: z.string().nullish(),
});
const CheckoutTreeNarySegmentNodeSchema: z.ZodType<CheckoutTreeNarySegmentNode> =
  CheckoutTreeNarySegmentNodeSchema_;
export interface CheckoutTreeNarySegmentNode extends z.infer<
  typeof CheckoutTreeNarySegmentNodeSchema_
> {}

const CheckoutTreeNarySegmentNodeOptionalIdSchema_ =
  CheckoutTreeNarySegmentNodeSchema_.partial({ _id: true })
    .omit({ edges: true })
    .extend({ edges: z.array(CheckoutTreeNarySegmentEdgeOptionalIdSchema) });
const CheckoutTreeNarySegmentNodeOptionalIdSchema: z.ZodType<CheckoutTreeNarySegmentNodeOptionalId> =
  CheckoutTreeNarySegmentNodeOptionalIdSchema_;
export interface CheckoutTreeNarySegmentNodeOptionalId extends z.infer<
  typeof CheckoutTreeNarySegmentNodeOptionalIdSchema_
> {}

// ───────────────────────────────────────────────────────────────────────────────
//   Root tree + parsing helpers
// ───────────────────────────────────────────────────────────────────────────────
const CheckoutTreeSchema_ = z.object({ rootNode: CheckoutTreeNodeSchema });
export const CheckoutTreeSchema: z.ZodType<CheckoutTree> = CheckoutTreeSchema_;
export interface CheckoutTree extends z.infer<typeof CheckoutTreeSchema_> {}

const CheckoutTreeOptionalIdsSchema_ = z.object({
  rootNode: CheckoutTreeNodeOptionalIdSchema,
});
const CheckoutTreeOptionalIdsSchema: z.ZodType<CheckoutTreeOptionalIds> =
  CheckoutTreeOptionalIdsSchema_;
export interface CheckoutTreeOptionalIds extends z.infer<
  typeof CheckoutTreeOptionalIdsSchema_
> {}

export function parseCheckoutTree(checkoutTree: unknown): CheckoutTree {
  return CheckoutTreeSchema.parse(checkoutTree);
}

export function safeParseCheckoutTree(
  checkoutTree: unknown,
): { success: true; value: CheckoutTree } | { success: false; error: Error } {
  const parsed = CheckoutTreeSchema.safeParse(checkoutTree);
  if (!parsed.success) {
    return { success: false, error: parsed.error };
  }
  return { success: true, value: parsed.data };
}

export function parseCheckoutTreeOptionalIds(
  checkoutTreeOptionalIds: unknown,
): CheckoutTreeOptionalIds {
  return CheckoutTreeOptionalIdsSchema.parse(checkoutTreeOptionalIds);
}

export function safeParseCheckoutTreeOptionalIds(
  checkoutTreeOptionalIds: unknown,
):
  | { success: true; value: CheckoutTreeOptionalIds }
  | { success: false; error: Error } {
  const parsed = CheckoutTreeOptionalIdsSchema.safeParse(
    checkoutTreeOptionalIds,
  );
  if (!parsed.success) {
    return { success: false, error: parsed.error };
  }
  return { success: true, value: parsed.data };
}

/**
 * A distillation of the information encoded in the checkout tree. Intended for
 * summarization of the checkout tree in the UI in places where it isn't
 * feasible to show the entire tree.
 */
const CheckoutTreeOverviewSchema_ = z.object({
  segments: z.array(z.object({ label: z.string() })),
  checkoutExperiences: z.array(z.object({ id: z.string(), label: z.string() })),
});
export const CheckoutTreeOverviewSchema: z.ZodType<CheckoutTreeOverview> =
  CheckoutTreeOverviewSchema_;
export interface CheckoutTreeOverview extends z.infer<
  typeof CheckoutTreeOverviewSchema_
> {}

export interface CheckoutTreeCreator {
  /**
   * Distinguishes this checkout tree creator type from any other string to
   * ensure the creator conforms to the correct string format.
   */
  __checkoutTreeCreator: true;
  value: string;
}

export function getCheckoutTreeCreatorFromUser(user: {
  _id: ObjectId;
}): CheckoutTreeCreator {
  return getCheckoutTreeCreator({ userType: "user", creator: user._id });
}

export function getCheckoutTreeCreator(
  params:
    | { userType: "user"; creator: ObjectId | string }
    | { userType: "admin" | "agent"; creator: string },
): CheckoutTreeCreator {
  switch (params.userType) {
    case "admin":
      return { __checkoutTreeCreator: true, value: `admin:${params.creator}` };
    case "user":
      return { __checkoutTreeCreator: true, value: `user:${params.creator}` };
    case "agent":
      return { __checkoutTreeCreator: true, value: `agent:${params.creator}` };
    default:
      assertNever(params);
  }
}

/**
 * If the actor/creator string is of the form `user:<id>`, returns the user ID.
 * Returns undefined for other formats (admin:, agent:, etc.).
 */
export function getUserIdFromActor(
  actor: string | undefined | null,
): string | undefined {
  if (!actor?.startsWith("user:")) {
    return undefined;
  }
  return actor.slice("user:".length);
}

/**
 * Maps a CheckoutTreeCreator (opaque prefixed string) to a TeamEventActor
 * variant for emission on team events. Returns undefined if the creator's
 * value doesn't match a known prefix.
 */
export function checkoutTreeCreatorToTeamEventActor(
  creator: CheckoutTreeCreator,
): Actor | undefined {
  const value = creator.value;
  if (value.startsWith("user:")) {
    return { type: "userId", userId: value.slice("user:".length) };
  }
  if (value.startsWith("admin:")) {
    return { type: "admin", email: value.slice("admin:".length) };
  }
  if (value.startsWith("agent:")) {
    return { type: "agent", agentId: value.slice("agent:".length) };
  }
  return undefined;
}

/**
 * Maps a universal {@link Actor} to the checkout-tree creator/actor used when
 * writing experiences/trees. `system` actors have no checkout-tree-creator
 * variant, so they map to an agent identified by their source.
 */
export function actorToCheckoutTreeCreator(actor: Actor): CheckoutTreeCreator {
  switch (actor.type) {
    case "admin":
      return getCheckoutTreeCreator({
        userType: "admin",
        creator: actor.email,
      });
    case "userId":
      return getCheckoutTreeCreator({
        userType: "user",
        creator: actor.userId,
      });
    case "agent":
      return getCheckoutTreeCreator({
        userType: "agent",
        creator: actor.agentId,
      });
    case "system":
      return getCheckoutTreeCreator({
        userType: "agent",
        creator: actor.source,
      });
    default:
      return assertNever(actor);
  }
}

/**
 * Segments that are forbidden in subscriptions mode.
 */
export const SUBSCRIPTIONS_MODE_FORBIDDEN_SEGMENTS = [
  SegmentableCheckoutCharacteristic.CUSTOMER_TAGS,
  SegmentableCheckoutCharacteristic.HISTORICAL_ORDER_VALUE,
] as const;
